<template>
  <div class="collection-filters">
    <v-menu
      v-model="showMenu"
      :close-on-content-click="false"
      offset-y
      left
      :min-width="280"
      content-class="collection-filters__menu-content"
    >
      <template #activator="{ on }">
        <div
          class="collection-filters__activator"
          v-on="on"
          @click="onActivatorClick"
        >
          <icons-settings-line color="#222325" />

          <span class="collection-filters__label">Filters</span>

          <cg-badge v-if="selectedCount > 0" color="#6E50FF">{{ selectedCount }}</cg-badge>
        </div>
      </template>

      <div class="collection-filters__menu">
        <cg-loader v-if="loading" />

        <template v-else>
          <template v-if="filteredCategories.length">
            <v-subheader class="collection-filters__subheader">Categories</v-subheader>

            <v-list dense class="collection-filters__list">
              <v-list-item
                v-for="item in filteredCategories"
                :key="item.id"
                :aria-selected="isSelected(item.id) ? 'true' : 'false'"
                class="select-multiple-entities__select--item"
                :ripple="false"
                @click="toggleItem(item.id)"
              >
                <div
                  class="select-multiple-entities__select--item--icon"
                  :class="{ 'select-multiple-entities__select--item--icon--checked': isSelected(item.id) }"
                />
                <div class="select-multiple-entities__select--item--name">
                  {{ item.name }} ({{ categoryCountMap[item.id] }})
                </div>
              </v-list-item>
            </v-list>
          </template>

          <template v-if="filteredInterests.length">
            <v-subheader class="collection-filters__subheader">Interests</v-subheader>

            <v-list dense class="collection-filters__list">
              <v-list-item
                v-for="item in filteredInterests"
                :key="item.id"
                :aria-selected="isSelected(item.id) ? 'true' : 'false'"
                class="select-multiple-entities__select--item"
                :ripple="false"
                @click="toggleItem(item.id)"
              >
                <div
                  class="select-multiple-entities__select--item--icon"
                  :class="{ 'select-multiple-entities__select--item--icon--checked': isSelected(item.id) }"
                />
                <div class="select-multiple-entities__select--item--name">
                  {{ item.name }} ({{ categoryCountMap[item.id] }})
                </div>
              </v-list-item>
            </v-list>
          </template>
          <div class="select-multiple-entities__actions">
            <div class="select-multiple-entities__action" @click="clearAll">Clear</div>
          </div>
        </template>
      </div>
    </v-menu>
  </div>
</template>

<script>
import Api from '@/axios/api'
import { CgBadge, CgLoader } from '@corporategiftcom/design-system'

export default {
  name: 'CreateCampaignCollectionFilters',
  components: {
    CgBadge,
    CgLoader,
  },
  props: {
    value: {
      type: Array,
      default: () => ([]),
    },
    themeId: {
      type: [Number, String],
      required: true,
    },
    products: {
      type: Array,
      default: () => ([]),
    },
  },
  data: () => ({
    lazyValue: [],
    showMenu: false,
    loading: false,
    fetched: false,
    categories: [],
    interests: [],
  }),
  computed: {
    modelValue: {
      get () { return this.lazyValue },
      set (val) {
        this.lazyValue = val
        this.$emit('input', val)
      },
    },
    selectedCount () {
      return this.lazyValue?.length ?? 0
    },
    categoryCountMap () {
      const map = {}
      for (const product of this.products) {
        for (const cat of (product.product?.categories ?? [])) {
          const id = Number(cat?.id ?? cat)
          map[id] = (map[id] ?? 0) + 1
        }
      }
      return map
    },
    filteredCategories () {
      return this.categories.filter((item) => (this.categoryCountMap[item.id] ?? 0) > 0)
    },
    filteredInterests () {
      return this.interests.filter((item) => (this.categoryCountMap[item.id] ?? 0) > 0)
    },
  },
  watch: {
    value (val) {
      this.lazyValue = val
    },
  },
  mounted () {
    this.lazyValue = this.value
  },
  methods: {
    onActivatorClick () {
      if (!this.fetched) { this.fetchOptions() }
    },
    fetchOptions () {
      this.loading = true
      Api.get(`/shared/collections/themes/${this.themeId}/filter-options`)
        .then(({ data }) => {
          this.categories = data.categories ?? []
          this.interests = data.interests ?? []
          this.fetched = true
        })
        .finally(() => (this.loading = false))
    },
    isSelected (id) {
      return this.lazyValue?.includes(id) ?? false
    },
    toggleItem (id) {
      const current = structuredClone(this.lazyValue ?? [])
      if (current.includes(id)) {
        this.modelValue = current.filter((i) => i !== id)
      } else {
        this.modelValue = [...current, id]
      }
    },
    clearAll () {
      this.modelValue = []
    },
  },
}
</script>

<style lang="scss" scoped>
.collection-filters {
  &__activator {
    padding: 4px 24px;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 10px;
    align-items: center;
    border-radius: 28px;
    background-color: #fff;
    border: 1px solid #D3D2D2;
    cursor: pointer;
  }

  &__label {
    font-family: 'Lato', sans-serif;
    font-weight: 600;
    line-height: 34px;
    font-size: 14px;
    color: #222325;
  }

  &__menu {
    background: #fff;
    max-height: 400px;
    overflow-y: auto;
  }

  &__subheader {
    font-family: 'Lato', sans-serif;
    font-size: 13px !important;
    font-weight: 700 !important;
    color: #000 !important;
    text-transform: uppercase;
  }

  &__list {
    padding: 0 !important;
  }

}
</style>

<style lang="scss">
.collection-filters__menu-content {
  .select-multiple-entities__action {
    color: #7B61FF;
  }

  .select-multiple-entities__select--item--icon--checked {
    background-color: #6E50FF !important;
    border-color: #6E50FF !important;

    &::after {
      position: absolute;
      left: 50%;
      top: 48%;
      transform: translate(-50%, -50%) rotate(45deg);
      content: ' ';
      width: 6px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
    }
  }

  .select-multiple-entities__select--item[aria-selected="true"] {
    background-color: #F6F5FF;
  }
}
</style>
